import { useState, useMemo, useCallback, useEffect } from "react";
import { unstable_batchedUpdates } from "react-dom";

import { COLUMNS_LABELS, DEFAULT_VALUES } from "../components/components.constants";

export const useHierarchyFilterViewModel = ({ actions, ctx, uiState }) => {

    const { 
        setSelectedProductIds,
        setSelectedHierarchyFilters,
        setAppliedFiltersCount,
        setSelectedSupplierFromFilter,
        setAppliedLevels: setExternalAppliedLevels,
        setSelectedLevels: setExternalSelectedLevels,
        setTypedExpression: setExternalTypedExpression,
        setAppliedExpression: setExternalAppliedExpression
    } = actions || {}     

    const { 
        suppliersAndProductRelations,
        hierarchyNames,
        productIds = [],
        allProducts = []
    } = ctx || {};

    const{ allowOneOptionForSelection } = uiState || {}

    const [_selectedLevels, _setSelectedLevels] = useState(uiState?.selectedLevels || {});
    const [_typedExpression, _setTypedExpression] = useState(uiState?.typedExpression || "");
    const [_appliedLevels, _setAppliedLevels] = useState(uiState?.appliedLevels || {});
    const [_appliedExpression, _setAppliedExpression] = useState(uiState?.appliedExpression || "");

    const selectedLevels = uiState?.selectedLevels ?? _selectedLevels;
    const typedExpression = uiState?.typedExpression ?? _typedExpression;
    const appliedLevels = uiState?.appliedLevels ?? _appliedLevels;
    const appliedExpression = uiState?.appliedExpression ?? _appliedExpression;

    const setSelectedLevels = useCallback((val) => { 
        _setSelectedLevels(val); 
        setExternalSelectedLevels?.(val); 
    }, [setExternalSelectedLevels]);

    const setTypedExpression = useCallback((val) => { 
        _setTypedExpression(val); 
        setExternalTypedExpression?.(val); 
    }, [setExternalTypedExpression]);

    const setAppliedLevels = useCallback((val) => { 
        _setAppliedLevels(val); 
        setExternalAppliedLevels?.(val); 
    }, [setExternalAppliedLevels]);

    const setAppliedExpression = useCallback((val) => { 
        _setAppliedExpression(val); 
        setExternalAppliedExpression?.(val); 
    }, [setExternalAppliedExpression]);

    /** Responsável pela criação das relações entre fornecedores e produtos. */
    const combinedProductsData = useMemo(() => {
        const map = new Map();

        for (const product of allProducts) {
            const relatedSuppliers =
            suppliersAndProductRelations?.filter(
                rel => rel.productId === product.productId
            ) || [];

            const suppliers = [
            ...new Set(
                relatedSuppliers
                .map(s => s.supplierName)
                .filter(Boolean)
            )
            ];

            map.set(product.productId, {
            ...product,
            suppliers: suppliers.length ? suppliers : [DEFAULT_VALUES.NOT_APPLY]
            });
        }

        return map;
    }, [allProducts, suppliersAndProductRelations]);

    const filteredProductIds = useMemo(() => {
        const hasFilters =
            Object.values(appliedLevels).some(arr => arr?.length) ||
            ((appliedExpression || "").trim().length);

        if (!hasFilters) return productIds;

        let currentFilteredIds = productIds;

        for (const level of hierarchyNames?.levels) {
            const levelValues = appliedLevels[level];

            if (levelValues?.length) {
                const valuesToFilter = allowOneOptionForSelection
                ? [levelValues[0]]
                : levelValues;

                currentFilteredIds = currentFilteredIds.filter(id => {
                const product = combinedProductsData.get(id);
                    return (
                        product &&
                        valuesToFilter.includes(product[level])
                    );
                });
            }
        }
        
        if (appliedLevels[COLUMNS_LABELS.SUPPLIER]?.length) {
            const suppliersToFilter = allowOneOptionForSelection
                ? [appliedLevels[COLUMNS_LABELS.SUPPLIER][0]]
                : appliedLevels[COLUMNS_LABELS.SUPPLIER];

            currentFilteredIds = currentFilteredIds?.filter(id => {
                const product = combinedProductsData.get(id);
                    return (
                    product &&
                    suppliersToFilter.some(supplier =>
                        product.suppliers.includes(supplier)
                    )
                );
            });
        }

        if ((appliedExpression || "").trim().length) {
            const expression = (appliedExpression || "").toUpperCase();
            currentFilteredIds = currentFilteredIds.filter(id => {
                const product = combinedProductsData.get(id);

                if (!product) return false; 

                const hasMatch = hierarchyNames?.levels.some(
                level => product[level]?.toUpperCase?.().includes(expression)
                );

                const hasSupplierMatch = (product.suppliers || []).some(
                supplier => supplier?.toUpperCase?.().includes(expression)
                );

                return hasMatch || hasSupplierMatch;
            });
        }

        return currentFilteredIds;
    }, [appliedLevels, productIds, combinedProductsData, appliedExpression, hierarchyNames]);

    const generateOptions = useCallback((filterType) => {
        let validProductIds = productIds || [];

        for (const level of hierarchyNames?.levels) {
            if (level !== filterType && selectedLevels[level]?.length) {
                const valuesSelect = allowOneOptionForSelection
                    ? [selectedLevels[level][0]]
                    : selectedLevels[level];

                validProductIds = validProductIds?.filter(id => {
                    const product = combinedProductsData.get(id);
                    return (
                        product &&
                        valuesSelect.includes(product[level])
                    );
                });
            }
        }

        if (filterType !== COLUMNS_LABELS.SUPPLIER && selectedLevels[COLUMNS_LABELS.SUPPLIER]?.length) {
            const suppliersSelect = allowOneOptionForSelection
            ? [selectedLevels[COLUMNS_LABELS.SUPPLIER][0]]
            : selectedLevels[COLUMNS_LABELS.SUPPLIER];

            validProductIds = validProductIds.filter(id => {
            const product = combinedProductsData.get(id);
            return (
                product &&
                suppliersSelect.some(s => product.suppliers.includes(s))
            );
            });
        }

        const expression = (typedExpression || "").trim().toUpperCase();
        if (expression.length) {
            validProductIds = validProductIds.filter(id => {
                const product = combinedProductsData.get(id);
                if (!product) return false;

                const hasMatch = hierarchyNames?.levels.some(
                    level => String(product[level] || "").toUpperCase().includes(expression)
                );

                const hasSupplierMatch = (product.suppliers || []).some(
                    supplier => String(supplier || "").toUpperCase().includes(expression)
                );

                return hasMatch || hasSupplierMatch;
            });
        }

        const dataToUse = validProductIds
            ?.map(id => combinedProductsData.get(id))
            .filter(Boolean) || [];

        if (filterType === COLUMNS_LABELS.SUPPLIER) {
            let allSuppliers = [...new Set(dataToUse.flatMap(p => p.suppliers))];

            if (allowOneOptionForSelection && selectedLevels[COLUMNS_LABELS.SUPPLIER]?.length) {
                allSuppliers = allSuppliers.filter(s => selectedLevels[COLUMNS_LABELS.SUPPLIER].includes(s));
            }

        return allSuppliers.map(name => ({ name })) || [];
    }

        const optionNames = [...new Set(dataToUse.map(p => p[filterType]))];
        return optionNames.map(name => ({ name })) || [];
    }, [
    productIds,
    combinedProductsData,
    selectedLevels,
    typedExpression,
    allowOneOptionForSelection
    ]);

    const getPossibleValues = useCallback((filterType) => {
        const dataToUse = filteredProductIds
            ?.map(id => combinedProductsData.get(id))
            .filter(Boolean) || [];

        if (filterType === COLUMNS_LABELS.SUPPLIER) {
            return [...new Set(dataToUse.flatMap(p => p.suppliers))];
        }

        return [...new Set(dataToUse.map(p => p[filterType]))];
    }, [
        filteredProductIds,
        combinedProductsData,
        allowOneOptionForSelection
    ]);

    const appliedFiltersNumber = useCallback(() => {
        const hierarchyCount = Object.values(appliedLevels)
            .filter(selected => selected?.length)
            .length;

        const expressionCount = (appliedExpression || "").trim().length ? 1 : 0;

        return hierarchyCount + expressionCount;
    }, [appliedLevels, appliedExpression]);

    const isAnyFilterApplied = () => appliedFiltersNumber() !== 0;

    const resetFilters = useCallback(() => {
        unstable_batchedUpdates(() => {
            setTypedExpression("");
            setSelectedLevels({});
            setAppliedExpression("");
            setAppliedLevels({});
        });
    }, [setTypedExpression, setSelectedLevels, setAppliedExpression, setAppliedLevels]);

    useEffect(() => {
        const safeFilteredIds = Array.isArray(filteredProductIds) ? filteredProductIds : [];

        setSelectedProductIds(prevIds => {
            if (prevIds.length !== safeFilteredIds.length) return safeFilteredIds;

            const isContentEqual = prevIds.every((id, index) => id === safeFilteredIds[index]);
            if (isContentEqual) return prevIds;

            return safeFilteredIds;
        });

        setAppliedFiltersCount(appliedFiltersNumber());
        setSelectedHierarchyFilters({
            selectedLevels: appliedLevels,
        });
        }, [
        filteredProductIds, 
        appliedLevels,
        appliedExpression,
        //setAppliedFiltersCount, 
        //setSelectedProductIds, 
        //setSelectedHierarchyFilters
    ]);

    useEffect(() => {
        const selectedSuppliers = appliedLevels[COLUMNS_LABELS.SUPPLIER] || [];
        
        if (selectedSuppliers.length === 1) {
        const supplierRelation = suppliersAndProductRelations.find(
            rel => rel.supplierName === selectedSuppliers[0]
        );
        
        if (supplierRelation) {
            setSelectedSupplierFromFilter({
            id: supplierRelation.supplierId,
            name: supplierRelation.supplierName
            });
        }
        } else {
        setSelectedSupplierFromFilter(null);
        }
    }, [appliedLevels, suppliersAndProductRelations, setSelectedSupplierFromFilter])

    return useMemo(() => ({
        actions: {
            setTypedExpression,
            resetFilters,
            isAnyFilterApplied,
            setSelectedLevels,
            setAppliedLevels,
            setAppliedExpression
        },
        ctx: {
            generateOptions,
            getPossibleValues,
            selectedLevels,
            filteredProductIds: filteredProductIds || {}
        },
        uiState: {
            typedExpression,
            appliedExpression,
            selectedLevels
        }
    }), [
        setTypedExpression, resetFilters, isAnyFilterApplied, setSelectedLevels, setAppliedLevels,
        generateOptions, getPossibleValues, setAppliedExpression,
        typedExpression, selectedLevels, appliedExpression
    ]);
}